import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Search, Plus, Download, Edit3, Trash2, X, ChevronLeft, ChevronRight, Filter, AlertTriangle, Container as ContainerIcon, FileText } from "lucide-react";
import ModuleTabs from "@/components/ModuleTabs";
import WaybillModal from "@/components/WaybillModal";

const TYPE_OPTIONS = ["20STD", "40STD", "40HC", "40RF", "20OT", "20RF", "40OT"];
const LOCATION_OPTIONS = ["MPS", "GPHA"];
const PAGE_SIZE = 25;

function fmt(n) { return n === null || n === undefined ? "—" : Number(n).toLocaleString(); }

const todayIso = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

export default function Records() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeSize, setTypeSize] = useState("ALL");
  const [location, setLocation] = useState("ALL");
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [waybillFor, setWaybillFor] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (typeSize !== "ALL") params.type_size = typeSize;
      if (location !== "ALL") params.location = location;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/containers", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      showToast("Failed to load records", "err");
    } finally {
      setLoading(false);
    }
  }, [skip, search, typeSize, location, dateFrom, dateTo]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Reset to page 1 when filters change
  useEffect(() => { setSkip(0); }, [search, typeSize, location, dateFrom, dateTo]);

  const handleSave = async (data) => {
    try {
      if (editing) {
        await api.put(`/containers/${editing.id}`, data);
        showToast("Container updated");
      } else {
        await api.post("/containers", data);
        showToast("Container added");
      }
      setShowForm(false);
      setEditing(null);
      fetchItems();
    } catch (e) {
      showToast("Save failed", "err");
    }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/containers/${confirmDel.id}`);
      showToast("Container deleted");
      setConfirmDel(null);
      fetchItems();
    } catch (e) {
      showToast("Delete failed", "err");
    }
  };

  const exportCsv = async () => {
    try {
      const params = { skip: 0, limit: 2000 };
      if (search) params.search = search;
      if (typeSize !== "ALL") params.type_size = typeSize;
      if (location !== "ALL") params.location = location;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/containers", { params });
      const rows = r.data.items;
      const header = ["Date", "Container No", "Type/Size", "Location", "Inspection (USD)", "Toll & Vann (GHC)"];
      const csv = [header.join(","), ...rows.map((x) =>
        [x.date, x.container_no, x.type_size, x.location, x.inspection_charge_usd, x.toll_vann_ghc].join(",")
      )].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `containers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      showToast(`Exported ${rows.length} records`);
    } catch {
      showToast("Export failed", "err");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const hasFilters = search || typeSize !== "ALL" || location !== "ALL" || dateFrom || dateTo;

  return (
    <div className="space-y-6" data-testid="records-page">
      <ModuleTabs module="inspection" />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2">Container Records</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#0a2952] tracking-tight leading-tight">
            All gate-in records.
          </h1>
          <p className="mt-2 text-slate-600">Search, filter, and manage every container that was direct gated in.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="export-csv-btn"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#0a2952] text-slate-700 px-4 py-2.5 rounded-md font-semibold transition-colors"
          >
            <Download size={16} /> Export CSV
          </button>
          <button
            type="button"
            data-testid="add-container-btn"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] text-white px-4 py-2.5 rounded-md font-semibold transition-all hover:-translate-y-[1px] hover:shadow"
          >
            <Plus size={16} /> Add Container
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
        <div className="lg:col-span-2 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            data-testid="filter-search"
            placeholder="Search container no..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none"
          />
        </div>
        <select
          data-testid="filter-type"
          value={typeSize}
          onChange={(e) => setTypeSize(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none bg-white"
        >
          <option value="ALL">All Types</option>
          {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          data-testid="filter-location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none bg-white"
        >
          <option value="ALL">All Locations</option>
          {LOCATION_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <input
          type="date"
          data-testid="filter-date-from"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none"
          placeholder="From"
        />
        <input
          type="date"
          data-testid="filter-date-to"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none"
          placeholder="To"
        />
        {hasFilters && (
          <button
            type="button"
            data-testid="clear-filters-btn"
            onClick={() => { setSearch(""); setTypeSize("ALL"); setLocation("ALL"); setDateFrom(""); setDateTo(""); }}
            className="md:col-span-2 lg:col-span-6 inline-flex items-center gap-1 text-xs text-[#ff5b2c] font-semibold hover:underline justify-self-start"
          >
            <X size={12} /> Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="records-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">
              <tr>
                <Th>Date</Th>
                <Th>Container No.</Th>
                <Th>Type / Size</Th>
                <Th>Location</Th>
                <Th align="right">Inspection (USD)</Th>
                <Th align="right">Toll & Vann (GHC)</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-testid="records-loading">Loading records...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center" data-testid="records-empty">
                  <div className="flex flex-col items-center gap-2 text-slate-500">
                    <ContainerIcon size={32} className="text-slate-300" />
                    <span className="font-semibold">No records match.</span>
                    <span className="text-xs">Try clearing filters or adding a new container.</span>
                  </div>
                </td></tr>
              )}
              {!loading && items.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`row-${row.id}`}>
                  <Td><span className="font-mono-tabular text-slate-700">{row.date}</span></Td>
                  <Td><span className="font-mono-tabular font-semibold text-[#0a2952]">{row.container_no}</span></Td>
                  <Td><span className="inline-block px-2 py-0.5 text-xs font-bold tracking-wide bg-slate-100 text-[#0a2952] rounded-sm">{row.type_size}</span></Td>
                  <Td>
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold tracking-wide rounded-sm ${
                      row.location === "MPS" ? "bg-[#0a2952]/10 text-[#0a2952]" : "bg-[#14b8a6]/15 text-[#0d8b80]"
                    }`}>
                      {row.location}
                    </span>
                  </Td>
                  <Td align="right"><span className="font-mono-tabular text-slate-700">{fmt(row.inspection_charge_usd)}</span></Td>
                  <Td align="right"><span className="font-mono-tabular text-slate-700">{fmt(row.toll_vann_ghc)}</span></Td>
                  <Td align="right">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        type="button"
                        data-testid={`waybill-btn-${row.id}`}
                        onClick={() => setWaybillFor(row)}
                        className="inline-flex items-center gap-1 px-2.5 h-8 rounded text-xs font-bold uppercase tracking-wide text-[#0a2952] hover:bg-[#0a2952]/10 transition-colors"
                        title="Generate waybill for this container"
                      >
                        <FileText size={13} /> Waybill
                      </button>
                      <button
                        type="button"
                        data-testid={`edit-btn-${row.id}`}
                        onClick={() => { setEditing(row); setShowForm(true); }}
                        className="w-8 h-8 grid place-items-center rounded text-slate-500 hover:bg-slate-100 hover:text-[#0a2952]"
                        title="Edit"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        type="button"
                        data-testid={`delete-btn-${row.id}`}
                        onClick={() => setConfirmDel(row)}
                        className="w-8 h-8 grid place-items-center rounded text-slate-500 hover:bg-red-50 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
          <div className="text-xs text-slate-600 font-mono-tabular" data-testid="pagination-info">
            {total === 0 ? "0 records" : `${skip + 1}–${Math.min(skip + PAGE_SIZE, total)} of ${fmt(total)}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="prev-page-btn"
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0}
              className="w-8 h-8 grid place-items-center rounded border border-slate-300 hover:border-[#0a2952] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs font-semibold text-slate-700 px-2 font-mono-tabular">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              data-testid="next-page-btn"
              onClick={() => setSkip(Math.min((totalPages - 1) * PAGE_SIZE, skip + PAGE_SIZE))}
              disabled={currentPage >= totalPages}
              className="w-8 h-8 grid place-items-center rounded border border-slate-300 hover:border-[#0a2952] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <ContainerForm
          initial={editing}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}
      {confirmDel && (
        <ConfirmDialog
          row={confirmDel}
          onCancel={() => setConfirmDel(null)}
          onConfirm={handleDelete}
        />
      )}
      <WaybillModal
        key={waybillFor?.id || "none"}
        open={!!waybillFor}
        container={waybillFor}
        onClose={() => setWaybillFor(null)}
        onIssued={(wb) => showToast(`Waybill ${wb.waybill_no} issued`)}
      />
      {toast && (
        <div
          data-testid="toast"
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-md shadow-lg text-sm font-semibold ${
            toast.kind === "err" ? "bg-red-600 text-white" : "bg-[#0a2952] text-white"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 text-${align} whitespace-nowrap`}>{children}</th>;
}
function Td({ children, align = "left" }) {
  return <td className={`px-4 py-3 text-${align} whitespace-nowrap`}>{children}</td>;
}

function ContainerForm({ initial, onCancel, onSave }) {
  const [form, setForm] = useState({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    container_no: initial?.container_no || "",
    type_size: initial?.type_size || "20STD",
    location: initial?.location || "MPS",
    inspection_charge_usd: initial?.inspection_charge_usd ?? 4,
    toll_vann_ghc: initial?.toll_vann_ghc ?? 35,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.container_no.trim()) { setErr("Container number is required"); return; }
    setSubmitting(true);
    setErr("");
    try { await onSave(form); }
    catch { setErr("Save failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="container-form-modal">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-lg border border-slate-200 shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="overline">{initial ? "Edit container" : "Add container"}</div>
            <div className="font-display font-extrabold text-xl text-[#0a2952] mt-1">
              {initial ? `Update ${initial.container_no}` : "New gate-in record"}
            </div>
          </div>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700" data-testid="form-close-btn">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {err && <div className="px-3 py-2 rounded bg-red-50 border border-red-200 text-sm text-red-700" data-testid="form-error">{err}</div>}
          <Field label="Date">
            <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="form-input" data-testid="form-date" />
          </Field>
          <Field label="Container Number">
            <input type="text" required value={form.container_no}
              onChange={(e) => setForm({ ...form, container_no: e.target.value.toUpperCase() })}
              placeholder="e.g. HLBU9781320" className="form-input font-mono-tabular" data-testid="form-container-no" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type / Size">
              <select value={form.type_size} onChange={(e) => setForm({ ...form, type_size: e.target.value })}
                className="form-input bg-white" data-testid="form-type">
                {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="form-input bg-white" data-testid="form-location">
                {LOCATION_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inspection Charge (USD)">
              <input type="number" step="0.01" min="0" value={form.inspection_charge_usd}
                onChange={(e) => setForm({ ...form, inspection_charge_usd: parseFloat(e.target.value || 0) })}
                className="form-input font-mono-tabular" data-testid="form-usd" />
            </Field>
            <Field label="Toll & Vann (GHC)">
              <input type="number" step="0.01" min="0" value={form.toll_vann_ghc}
                onChange={(e) => setForm({ ...form, toll_vann_ghc: parseFloat(e.target.value || 0) })}
                className="form-input font-mono-tabular" data-testid="form-ghc" />
            </Field>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onCancel}
            className="px-4 py-2 rounded border border-slate-300 hover:border-slate-500 text-sm font-semibold" data-testid="form-cancel-btn">
            Cancel
          </button>
          <button type="submit" disabled={submitting}
            className="px-5 py-2 rounded bg-[#ff5b2c] hover:bg-[#e54f24] text-white text-sm font-semibold disabled:opacity-60" data-testid="form-submit-btn">
            {submitting ? "Saving..." : initial ? "Update" : "Add Container"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function ConfirmDialog({ row, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="confirm-dialog">
      <div className="bg-white rounded-md w-full max-w-md border border-slate-200 shadow-2xl">
        <div className="p-6">
          <div className="w-12 h-12 rounded-full bg-red-50 grid place-items-center mb-4">
            <AlertTriangle className="text-red-600" />
          </div>
          <div className="font-display font-extrabold text-xl text-[#0a2952] mb-2">Delete container?</div>
          <p className="text-sm text-slate-600">
            This will permanently remove <span className="font-mono-tabular font-bold text-[#0a2952]">{row.container_no}</span> from {row.date}. This cannot be undone.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded border border-slate-300 text-sm font-semibold" data-testid="confirm-cancel-btn">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-semibold" data-testid="confirm-delete-btn">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
